/**
 * Module catalogue HTTP service (PX-W6.5) — the catalogue reads and the
 * per-project attachment verbs.
 *
 * ⚠ READ-ONLY catalogue (INV-5): no verb here targets
 * `/modules/definitions*`. Rows are seeded from `conf/px-module-catalogue.json`
 * and rewritten on every boot — a runtime write would be silently reverted.
 * The only writes are the per-project PATCH attachment routes.
 *
 * ⚠ The `:key` of the questionnaire route is the lower-kebab `moduleId`, NOT
 * an ObjectId (`ModuleDefinitionController.questionnaire(request, key)`).
 *
 * All URLs ride the shared `apiClient`; `{orgId}` is replaced by the
 * transport's registered session getter.
 */

import { apiClient } from '@/services'

import type {
  ModuleDefinition,
  ModuleQuestionnaire,
  ProjectModule,
  ProjectModulePatch,
  ProjectOption
} from '../types'

export const moduleCatalogueService = {
  /** The whole workspace catalogue (org-less, read-only). */
  listDefinitions(): Promise<ModuleDefinition[]> {
    return apiClient.get<ModuleDefinition[]>('/modules/definitions')
  },

  /** One module's questionnaire, by its lower-kebab `moduleId` key. */
  getQuestionnaire(key: string): Promise<ModuleQuestionnaire> {
    return apiClient.get<ModuleQuestionnaire>(
      `/modules/definitions/${encodeURIComponent(key)}/questionnaire`
    )
  },

  /**
   * The picker's private `{id, name}` projection. Deliberately this feature's
   * own service (not a shared `features/projects` file — W6.3 owns that name).
   */
  listProjects(): Promise<ProjectOption[]> {
    return apiClient.get<ProjectOption[]>('/{orgId}/projects')
  },

  /** The project's attachment rows (enabled flag, answers, config). */
  listProjectModules(projectId: string): Promise<ProjectModule[]> {
    return apiClient.get<ProjectModule[]>(
      `/{orgId}/projects/${encodeURIComponent(projectId)}/modules`
    )
  },

  /**
   * Sparse update of ONE attachment row. The body carries only the changed
   * fields — the server reads `enabled`/`configuration`/`userAnswers`/
   * `implementationNotes` and nothing else.
   */
  patchProjectModule(
    projectId: string,
    moduleId: string,
    patch: ProjectModulePatch
  ): Promise<ProjectModule> {
    return apiClient.patch<ProjectModule>(
      `/{orgId}/projects/${encodeURIComponent(projectId)}/modules/${encodeURIComponent(moduleId)}`,
      patch
    )
  }
}
